import { type ReactNode, useEffect, useRef } from 'react'
import { motion, useAnimationControls } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { transition } from '@/lib/motion'
import { useReducedMotion } from '@/hooks/useReducedMotion'

interface PageTransitionProps {
  children: ReactNode
  className?: string
}

/**
 * Cross-fades page content on navigation.
 *
 * TWO scopes, deliberately different:
 *
 *  • HUB CHANGE (/tenant/build → /tenant/ai) — Layout passes `key={sectionKey}`,
 *    which REMOUNTS this component. The mount branch below runs the full fade.
 *
 *  • SUB-TAB CHANGE (/tenant/build/workflows → /tenant/build/agents) — the key
 *    is unchanged, so nothing remounts and, before this, nothing animated: the
 *    content simply snapped. The effect re-runs the fade on any pathname
 *    change WITHOUT remounting, which is the point — remounting here would
 *    tear down in-flight SSE on the chat route and reset scroll (the
 *    regression fixed in 1ffa5dff5). The sub-tab fade is shorter and starts
 *    from a partial opacity, so it reads as a settle rather than a reload:
 *    a sub-tab is a low-context-shift move an operator makes dozens of times
 *    a session and must not feel like waiting.
 *
 * OPACITY ONLY — deliberately not a slide. Framer Motion implements `y` as an
 * inline transform and leaves it applied at rest (`translateY(0px)`), and ANY
 * non-`none` transform makes the element the containing block for its
 * `position: fixed` descendants. Because this wrapper hosts every route, a
 * transform here would re-anchor every fixed overlay inside a page — the ~15
 * non-portaled `fixed inset-0` modals and the Discover panel's reopen control
 * — to this wrapper instead of the viewport, pushing them off-screen inside
 * <main>'s scroll region. Opacity creates no containing block.
 */
export function PageTransition({ children, className }: PageTransitionProps) {
  const prefersReducedMotion = useReducedMotion()
  const { pathname } = useLocation()
  const controls = useAnimationControls()
  const isMount = useRef(true)

  useEffect(() => {
    if (prefersReducedMotion) {
      controls.set({ opacity: 1 })
      isMount.current = false
      return
    }
    // Mount = a real section change (Layout remounts us on hub change), so the
    // fade starts from nothing. A sub-tab change starts most of the way there.
    controls.set({ opacity: isMount.current ? 0 : 0.55 })
    void controls.start({
      opacity: 1,
      transition: isMount.current ? transition.normal : transition.fast,
    })
    isMount.current = false
  }, [pathname, controls, prefersReducedMotion])

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={controls} className={className}>
      {children}
    </motion.div>
  )
}
